import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";

interface ApprovalEvent {
  event_type: string;
  comment?: string | null;
  created_at: string;
  actor_email?: string;
}

interface ApprovalPrintData {
  title: string;
  request_type: string;
  status: string;
  description?: string | null;
  urgency?: string | null;
  project_name?: string;
  company_name?: string;
  company_logo_url?: string | null;
  created_at: string;
  resolved_at?: string | null;
  resolution_note?: string | null;
  events?: ApprovalEvent[];
}

export const ApprovalPrintTemplate = ({ data }: { data: ApprovalPrintData }) => (
  <div className="print-template p-8 max-w-[8.5in] mx-auto font-sans text-sm leading-relaxed">
    <div className="flex justify-between items-start border-b pb-4 mb-6">
      <div>
        {data.company_logo_url && (
          <img src={data.company_logo_url} alt={data.company_name ?? "Company"} style={{ maxHeight: "40px", objectFit: "contain", marginBottom: "4px" }} />
        )}
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#111" }}>APPROVAL SUMMARY</h1>
        {data.company_name && (
          <p className="text-base font-medium mt-1" style={{ color: "#333" }}>{data.company_name}</p>
        )}
      </div>
      <div className="text-right">
        <p className="capitalize text-xs font-semibold px-2 py-0.5 rounded inline-block" style={{ background: "#f3f4f6", color: "#374151" }}>
          {data.status}
        </p>
        {data.urgency === "urgent" && (
          <p className="text-xs font-bold mt-1" style={{ color: "#dc2626" }}>⚠ URGENT</p>
        )}
      </div>
    </div>

    <div className="mb-6">
      <h2 className="text-lg font-bold mb-4" style={{ color: "#111" }}>{data.title}</h2>
      <div className="grid grid-cols-3 gap-6">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Type</p>
          <p className="capitalize">{data.request_type.replace(/_/g, " ")}</p>
        </div>
        {data.project_name && (
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Project</p>
            <p>{data.project_name}</p>
          </div>
        )}
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Requested</p>
          <p>{format(new Date(data.created_at), "MMMM d, yyyy")}</p>
        </div>
      </div>
    </div>

    {data.description && (
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "#999" }}>Description</p>
        <div className="p-4 rounded" style={{ background: "#f9fafb", border: "1px solid #e5e7eb" }}>
          <p style={{ whiteSpace: "pre-wrap" }}>{data.description}</p>
        </div>
      </div>
    )}

    {data.resolution_note && (
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: "#999" }}>Resolution Note</p>
        <p>{data.resolution_note}</p>
      </div>
    )}

    {data.resolved_at && (
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "#999" }}>Resolved</p>
        <p>{format(new Date(data.resolved_at), "MMMM d, yyyy 'at' h:mm a")}</p>
      </div>
    )}

    {/* Event History */}
    {data.events && data.events.length > 0 && (
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider font-semibold mb-3" style={{ color: "#999" }}>History</p>
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #ddd" }}>
              <th className="text-left py-2 text-xs uppercase" style={{ color: "#666" }}>Event</th>
              <th className="text-left py-2 text-xs uppercase" style={{ color: "#666" }}>User</th>
              <th className="text-left py-2 text-xs uppercase" style={{ color: "#666" }}>Comment</th>
              <th className="text-right py-2 text-xs uppercase" style={{ color: "#666" }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((ev, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td className="py-2 capitalize">{ev.event_type.replace(/_/g, " ")}</td>
                <td className="py-2">{ev.actor_email || "—"}</td>
                <td className="py-2" style={{ color: "#666" }}>{ev.comment || "—"}</td>
                <td className="py-2 text-right">{format(new Date(ev.created_at), "MMM d, yyyy h:mm a")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    <CommandXFooter />
  </div>
);
